import { withJsonFormsControlProps } from "@jsonforms/react"
import {
  ControlProps,
  JsonFormsRendererRegistryEntry,
  rankWith,
  scopeEndsWith,
  or,
  and,
  schemaMatches,
  JsonSchema,
} from "@jsonforms/core"
import { Form, InputNumber } from "antd"

export const PROPORTION_RANGE_ERROR = "Proportions must fall between 0 and 1."
export const PROPORTION_CROSS_FIELD_ERROR = "Maximum proportion can not exceed minimum proportion."
export const MIN_PROPORTION_LABEL = "Min Proportion"
export const MAX_PROPORTION_LABEL = "Max Proportion"
export const MIN_PROPORTION_PATH = "min_value"
export const MAX_PROPORTION_PATH = "max_value"

function isBlank(val: unknown) {
  return val === undefined || val === null || val === ""
}

function ProportionMinMaxControl(props: ControlProps) {
  const form = Form.useFormInstance()

  if (!props.visible) return null

  const { handleChange } = props

  const onChangeImmediate = (val: string | number | null) => {
    handleChange(props.path, val === null ? undefined : val)

    // Cross-field validation: always revalidate both min and max when either changes
    const base = props.path.replace(/min_value|max_value/, "")
    const minPath = base + MIN_PROPORTION_PATH
    const maxPath = base + MAX_PROPORTION_PATH
    form.validateFields([minPath, maxPath]).catch(() => {})
  }

  const onBlurClamp = () => {
    const raw = form.getFieldValue(props.path)
    const numeric = +raw
    if (Number.isNaN(numeric)) return

    let adjusted = numeric
    if (numeric < 0) adjusted = 0
    if (numeric > 1) adjusted = 1

    if (adjusted !== numeric) {
      form.setFieldsValue({ [props.path]: adjusted })
      handleChange(props.path, adjusted)
    }
  }

  let ariaLabel: string | undefined
  if (typeof props.label === "string" && props.label) {
    ariaLabel = props.label
  } else if (props.path.endsWith(MIN_PROPORTION_PATH)) {
    ariaLabel = MIN_PROPORTION_LABEL
  } else if (props.path.endsWith(MAX_PROPORTION_PATH)) {
    ariaLabel = MAX_PROPORTION_LABEL
  }

  return (
    <Form.Item
      label={props.label}
      id={props.id}
      name={props.path}
      required={props.required}
      initialValue={props.data}
      rules={[
        {
          validator: (_, value) => {
            if (value === undefined || value === "") return Promise.resolve()

            const numericVal = +value
            if (Number.isNaN(numericVal)) return Promise.resolve()

            if (numericVal < 0 || numericVal > 1) {
              return Promise.reject(PROPORTION_RANGE_ERROR)
            }

            const base = props.path.replace(/min_value|max_value/, "")
            const minRaw = form.getFieldValue(base + MIN_PROPORTION_PATH)
            const maxRaw = form.getFieldValue(base + MAX_PROPORTION_PATH)
            if (isBlank(minRaw) || isBlank(maxRaw)) {
              return Promise.resolve()
            }
            const minVal = +minRaw
            const maxVal = +maxRaw
            if (!Number.isNaN(minVal) && !Number.isNaN(maxVal) && maxVal < minVal) {
              return Promise.reject(PROPORTION_CROSS_FIELD_ERROR)
            }
            return Promise.resolve()
          },
        },
      ]}
      validateTrigger={["onChange", "onBlur"]}
    >
      <InputNumber
        step={0.01}
        style={{ width: "100%" }}
        aria-label={ariaLabel}
        onChange={onChangeImmediate}
        onBlur={onBlurClamp}
      />
    </Form.Item>
  )
}

export const ProportionMinMaxRegistryEntry: JsonFormsRendererRegistryEntry = {
  tester: rankWith(
    // Higher than default NumericControl and MinMaxControl
    95,
    and(
      or(scopeEndsWith("#/properties/min_value"), scopeEndsWith("#/properties/max_value")),
      schemaMatches((schema: JsonSchema) => {
        if (!schema) return false
        const desc =
          typeof (schema as { description?: unknown }).description === "string"
            ? (schema as { description: string }).description
            : ""
        const title =
          typeof (schema as { title?: unknown }).title === "string" ? (schema as { title: string }).title : ""
        return desc.toLowerCase().includes("proportion") || title.toLowerCase().includes("proportion")
      }),
    ),
  ),
  renderer: withJsonFormsControlProps(ProportionMinMaxControl),
}
